import { useRef, useState, type CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { App, Image as AntdImage } from 'antd'
import {
  CloseOutlined,
  DownloadOutlined,
  EyeOutlined,
  LoadingOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { uploadScreenshot } from '@/api/versions'
import { MAX_SCREENSHOTS } from '../languages'

const MAX_SIZE = 5 * 1024 * 1024
/** 上传框统一 80×80（PC / Mobile 一致） */
const UPLOAD_BOX = 80

/** PC 16:9（允许 16:10）；移动端 9:16（允许 9:19.5） */
function ratioOk(kind: 'pc' | 'mobile', w: number, h: number): boolean {
  const r = w / h
  if (kind === 'pc') {
    return r >= 1.6 * 0.95 && r <= (16 / 9) * 1.05
  }
  return r >= (9 / 19.5) * 0.95 && r <= (9 / 16) * 1.05
}

function fileNameFromUrl(src: string, index: number): string {
  try {
    const name = decodeURIComponent(new URL(src, window.location.href).pathname)
      .split('/')
      .pop()
      ?.split('?')[0]
    if (name && /\.[a-z0-9]+$/i.test(name)) return name
  } catch {
    // blob: 或非法地址走默认名
  }
  return `screenshot-${index + 1}.png`
}

async function downloadImage(src: string, index: number) {
  const name = fileNameFromUrl(src, index)
  const save = (href: string) => {
    const a = document.createElement('a')
    a.href = href
    a.download = name
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    a.remove()
  }
  try {
    const res = await fetch(src)
    if (!res.ok) throw new Error(String(res.status))
    const url = URL.createObjectURL(await res.blob())
    save(url)
    // 立刻释放的话，部分浏览器还没开始下载，文件会空掉
    window.setTimeout(() => URL.revokeObjectURL(url), 1500)
  } catch {
    // 跨域读不到文件时，新开页让浏览器自己保存，避免把当前页跳走
    window.open(src, '_blank', 'noopener')
  }
}

function defaultTitle(file: File, index: number): string {
  const base = file.name.replace(/\.[^.]+$/, '').trim()
  return base || `Screenshot ${index + 1}`
}

export default function ScreenshotUploader({
  kind,
  value,
  disabled,
  app_id,
  version_id,
  lang,
  onChange,
}: {
  kind: 'pc' | 'mobile'
  value: string[]
  disabled?: boolean
  app_id: string
  version_id: string
  lang: string
  onChange: (next: string[]) => void
}) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [preview, setPreview] = useState({ open: false, current: 0 })

  // 预览缩略图：PC 横图、Mobile 竖图；上传框尺寸两边一致
  const thumbW = kind === 'pc' ? 136 : 80
  const thumbH = kind === 'pc' ? 80 : 173

  const handleFiles = (files: FileList | null) => {
    if (!files || uploading) return
    const queue = Array.from(files)
    void (async () => {
      setUploading(true)
      let next = value.slice()
      try {
        for (const file of queue) {
          if (next.length >= MAX_SCREENSHOTS) break
          if (file.size > MAX_SIZE) {
            message.error(t('versionForm.screenshotTooLarge'))
            continue
          }

          const previewUrl = URL.createObjectURL(file)
          const ok = await new Promise<boolean>((resolve) => {
            const img = new Image()
            img.onload = () => {
              const pass = ratioOk(kind, img.width, img.height)
              URL.revokeObjectURL(previewUrl)
              resolve(pass)
            }
            img.onerror = () => {
              URL.revokeObjectURL(previewUrl)
              resolve(false)
            }
            img.src = previewUrl
          })
          if (!ok) {
            message.error(
              t(kind === 'pc' ? 'versionForm.ratioPc' : 'versionForm.ratioMobile'),
            )
            continue
          }

          try {
            const res = await uploadScreenshot({
              app_id,
              version_id,
              file,
              kind,
              lang,
              title: defaultTitle(file, next.length),
            })
            if (res.url) next = [...next, res.url]
          } catch (err: unknown) {
            message.error(err instanceof Error ? err.message : String(err))
          }
        }
        onChange(next)
      } finally {
        setUploading(false)
      }
    })()
  }

  return (
    <AntdImage.PreviewGroup
      items={value}
      preview={{
        visible: preview.open,
        current: preview.current,
        onVisibleChange: (open) => setPreview((p) => ({ ...p, open })),
        onChange: (current) => setPreview((p) => ({ ...p, current })),
      }}
    >
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start' }}>
      {!disabled && value.length < MAX_SCREENSHOTS && (
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          style={{
            width: UPLOAD_BOX,
            height: UPLOAD_BOX,
            borderRadius: 8,
            border: '1px solid var(--color-border-line)',
            background: 'rgba(0,0,0,0.04)',
            cursor: uploading ? 'wait' : 'pointer',
            color: '#000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '10px 20px',
            flexShrink: 0,
            opacity: uploading ? 0.85 : 1,
          }}
        >
          {uploading ? (
            <LoadingOutlined style={{ fontSize: 18 }} spin />
          ) : (
            <PlusOutlined style={{ fontSize: 18 }} />
          )}
        </button>
      )}
      {value.map((src, index) => (
        <ScreenshotThumb
          key={src}
          src={src}
          index={index}
          width={thumbW}
          height={thumbH}
          bordered={kind === 'pc'}
          removable={!disabled}
          onRemove={() => onChange(value.filter((x) => x !== src))}
          onPreview={() => setPreview({ open: true, current: index })}
        />
      ))}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => {
          handleFiles(e.target.files)
          e.target.value = ''
        }}
      />
    </div>
    </AntdImage.PreviewGroup>
  )
}

function ScreenshotThumb({
  src,
  index,
  width,
  height,
  bordered,
  removable,
  onRemove,
  onPreview,
}: {
  src: string
  index: number
  width: number
  height: number
  bordered: boolean
  removable: boolean
  onRemove: () => void
  onPreview: () => void
}) {
  const { t } = useTranslation()
  const [hover, setHover] = useState(false)

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        width,
        height,
        borderRadius: 8,
        overflow: 'hidden',
        border: bordered ? '1px solid var(--color-border-line)' : 'none',
        flexShrink: 0,
      }}
    >
      <img
        src={src}
        alt=""
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
      {removable && (
        <button
          type="button"
          onClick={onRemove}
          style={{ ...thumbBtnStyle(), position: 'absolute', top: 2, right: 2 }}
        >
          <CloseOutlined />
        </button>
      )}
      {hover && (
        <div
          style={{
            position: 'absolute',
            right: 2,
            bottom: 2,
            display: 'flex',
            gap: 4,
          }}
        >
          <button
            type="button"
            aria-label={t('common.preview')}
            title={t('common.preview')}
            onClick={onPreview}
            style={thumbBtnStyle()}
          >
            <EyeOutlined />
          </button>
          <button
            type="button"
            aria-label={t('common.download')}
            title={t('common.download')}
            onClick={() => void downloadImage(src, index)}
            style={thumbBtnStyle()}
          >
            <DownloadOutlined />
          </button>
        </div>
      )}
    </div>
  )
}

function thumbBtnStyle(): CSSProperties {
  return {
    width: 18,
    height: 18,
    borderRadius: 9,
    border: 'none',
    background: 'rgba(0,0,0,0.5)',
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
    padding: 0,
  }
}
